import { ChevronDown } from "lucide-mui";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type ReactNode, useState } from "react";
import LogoBadge from "./LogoBadge.tsx";
import StatusIcon, { type Status } from "./StatusIcon.tsx";
import { formatNumber } from "../utils/format.ts";

interface AccordionCardProps {
  /** A brand mark or icon, drawn in a `LogoBadge`. */
  logo?: ReactNode;
  title: ReactNode;
  /** A grey number after the title (members, rows). */
  count?: number;
  /** One line under the title: what's connected, or what it does. */
  summary?: ReactNode;
  status?: Status;
  /**
   * Open the card. Starts it open, and opens it again whenever this turns true
   * (e.g. a connection turns out broken once loaded); never closes it, so a
   * card someone is working in doesn't snap shut when its problem clears.
   */
  defaultExpanded?: boolean;
  /**
   * Content runs edge to edge under a divider (a table, a list of rows);
   * without it the content sits in the card's padding.
   */
  disablePadding?: boolean;
  children: ReactNode;
}

/**
 * One configurable thing (an integration, a data provider): status at a glance,
 * settings on demand. Stack several with `<Stack spacing={1.5}>`.
 */
export default function AccordionCard({
  logo,
  title,
  count,
  summary,
  status,
  defaultExpanded = false,
  disablePadding = false,
  children,
}: AccordionCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [prevDefault, setPrevDefault] = useState(defaultExpanded);
  if (defaultExpanded !== prevDefault) {
    setPrevDefault(defaultExpanded);
    if (defaultExpanded) setExpanded(true);
  }

  return (
    <Accordion expanded={expanded} onChange={(_, open) => setExpanded(open)}>
      <AccordionSummary expandIcon={<ChevronDown />} sx={{ px: 2, py: 0.5 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flex: 1, minWidth: 0, mr: 1 }}>
          {logo && <LogoBadge>{logo}</LogoBadge>}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
              {count != null && (
                <Typography component="span" variant="inherit" sx={{ color: "text.secondary", fontWeight: 500, ml: 1 }}>
                  {formatNumber(count)}
                </Typography>
              )}
            </Typography>
            {summary && (
              <Typography variant="body2" noWrap sx={{ color: "text.secondary" }}>
                {summary}
              </Typography>
            )}
          </Box>
          {status && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexShrink: 0 }}>
              <StatusIcon kind={status.kind} />
              {status.label && (
                <Typography
                  variant="body2"
                  sx={{
                    color: status.kind === "attention" || status.kind === "error" ? "text.primary" : "text.secondary",
                    fontWeight: status.kind === "attention" || status.kind === "error" ? 600 : undefined,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {status.label}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={disablePadding ? { p: 0, borderTop: 1, borderColor: "divider" } : { px: 2, pb: 2, pt: 0 }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
